import React from "react";
import { EDGE } from "./edgeTokens";

// The reusable Edge feedback states, drawn at one point on a chart. Each state
// is told apart by SHAPE as well as colour, so it survives without colour vision
// and with reduced motion (no state depends on an animation to be read):
//
//   neutral    — Call It placement: a grey diamond centred on the literal tap,
//                wherever it is (client: "One marker for 'your placement'
//                everywhere"). Not moved beside the candle: there is no known
//                position to point at yet, only where the learner tapped.
//   correct    — Correct: a clean teal check mark. No fill, no container, and
//                never gold (client: "a check never appears in gold").
//
// Diamonds — one shape, one placement rule for every element (client: "every
// gold diamond sits beside the candle, never on top of it. Its horizontal point
// touches the exact pixel of the position it marks… The diamond body extends
// sideways into clear space"). (cx, cy) is that touching point; `side` says
// which way the body extends.
//   teach      — gold diamond: an element placed in Teach.
//   target     — gold diamond with a soft halo: the Teach target.
//   corrected  — gold diamond: the correct answer.
//   incorrect  — grey diamond: the learner's own (wrong) tap. Never red.
//
//   miss       — Guided soft cue: grey ring that fades out.
export const MARKER_STYLE = {
  // Labels and connectors are structure: faint grey. Colour is carried by the
  // marker alone.
  neutral: { chipStroke: EDGE.line, sublabel: null },
  teach: { chipStroke: EDGE.line, sublabel: null },
  correct: { chipStroke: EDGE.line, sublabel: "Correct" },
  incorrect: { chipStroke: EDGE.line, sublabel: "Your answer", dashed: true },
  corrected: { chipStroke: EDGE.line, sublabel: "Correct answer" },
};

export const DIAMOND_STATES = ["teach", "target", "corrected", "incorrect"];
// Half the diamond's width: the far point is 2 * DIAMOND_D from the touching point.
export const DIAMOND_D = 7;

export default function FeedbackMarker({ cx, cy, state, delay = 0, side = "right" }) {
  const style = delay ? { animationDelay: `${delay}ms` } : undefined;
  switch (state) {
    case "neutral":
      return <Diamond cx={cx - DIAMOND_D} cy={cy} side="right" fill={EDGE.answer} />;
    case "correct":
      return (
        <g className="edge-pulse" style={style}>
          {/* A white halo under the stroke keeps the check legible where it
              sits on the candle; on the white canvas it is invisible. */}
          <path d={checkPath(cx, cy)} fill="none" stroke="#fff" strokeWidth={5} strokeLinecap="round" strokeLinejoin="round" />
          <path d={checkPath(cx, cy)} fill="none" stroke={EDGE.teal} strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" />
        </g>
      );
    case "teach":
      return <Diamond cx={cx} cy={cy} side={side} fill={EDGE.gold} className="edge-fade-in" />;
    case "corrected":
      return <Diamond cx={cx} cy={cy} side={side} fill={EDGE.gold} className="edge-pulse" style={style} />;
    case "incorrect":
      return <Diamond cx={cx} cy={cy} side={side} fill={EDGE.answer} />;
    case "target": {
      const dir = side === "left" ? -1 : 1;
      return (
        <g pointerEvents="none">
          <circle cx={cx + dir * DIAMOND_D} cy={cy} r={13} fill={EDGE.gold} fillOpacity={0.3} className="edge-hint" />
          <Diamond cx={cx} cy={cy} side={side} fill={EDGE.gold} />
        </g>
      );
    }
    case "miss":
      return <circle cx={cx} cy={cy} r={9} fill="none" stroke={EDGE.neutral} strokeWidth={2} className="edge-miss" />;
    default:
      return null;
  }
}

// Diamond whose horizontal point sits exactly on (cx, cy), body extending to
// `side`. No outline.
export function Diamond({ cx, cy, side, fill, className, style }) {
  const d = DIAMOND_D;
  const dir = side === "left" ? -1 : 1;
  const pts = [
    [cx, cy],
    [cx + dir * d, cy - d],
    [cx + dir * 2 * d, cy],
    [cx + dir * d, cy + d],
  ]
    .map((p) => p.join(","))
    .join(" ");
  return <polygon points={pts} fill={fill} className={className} style={style} />;
}

const checkPath = (cx, cy) => `M${cx - 5} ${cy + 0.3} L${cx - 1.4} ${cy + 3.9} L${cx + 5.2} ${cy - 3.6}`;
